import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import AssignModal from "../components/AssignModal.jsx";
import ParaForm from "../components/ParaForm.jsx";
import ZoneOfficerAssignModal from "../components/ZoneOfficerAssignModal.jsx";
import "../styles/ParaDetail.css";

// Workflow statuses — admins may override the status manually here.
const PARA_STATUSES = [
  ["PENDING", "Pending"],
  ["IN_PROGRESS", "In Progress"],
  ["REPLY_SUBMITTED", "Reply Submitted"],
  ["RESOLVED", "Resolved"],
  ["CLOSED", "Closed"],
  ["REOPENED", "Reopened"],
];

function formatINR(n) {
  if (n === null || n === undefined || n === "") return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}
function fmtDate(d) {
  return d ? new Date(d).toLocaleString("en-IN") : "—";
}

export default function ParaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();

  const [para, setPara] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [showAssign, setShowAssign] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [msg, setMsg] = useState("");
  const [translating, setTranslating] = useState(false);
  const [activeZoneLink, setActiveZoneLink] = useState(null);
  const [zonePopup, setZonePopup] = useState(null); // { zone, rect } for the floating detail popup
  const zonePopupRef = useRef(null);

  // Close zone popup when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (zonePopupRef.current && !zonePopupRef.current.contains(e.target)) {
        setZonePopup(null);
      }
    };
    if (zonePopup) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [zonePopup]);

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const response = await api.post(`/paras/${id}/translate/`);
      setPara(response.data);
      flash("விவரங்கள் வெற்றிகரமாக மொழிபெயர்க்கப்பட்டன.");
    } catch (e) {
      console.error(e);
      window.alert(e.response?.data?.detail || "மொழிபெயர்ப்பு தோல்வியடைந்தது.");
    } finally {
      setTranslating(false);
    }
  };

  const canAssign = ["SUPER_ADMIN", "CORP_ADMIN", "ZONAL_AC"].includes(user.role);
  const canClose = ["SUPER_ADMIN", "AUDITOR"].includes(user.role);
  const isAdmin = ["SUPER_ADMIN", "CORP_ADMIN"].includes(user.role);

  const load = useCallback(() => {
    api
      .get(`/paras/${id}/`)
      .then((r) => {
        setPara(r.data);
        setLoadError("");
      })
      .catch((e) => {
        setLoadError(
          e.response?.status === 404
            ? "This para was not found, or it is not assigned to your zone."
            : "Could not load this para. Please try again."
        );
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 3000);
  };

  if (loadError) {
    return (
      <div>
        <button className="btn btn-sm" onClick={() => navigate("/paras")}>
          ← {t("paras")}
        </button>
        <div className="card" style={{ marginTop: 12 }}>
          <p className="muted" style={{ margin: 0 }}>
            {loadError}
          </p>
        </div>
      </div>
    );
  }
  if (!para) return <div className="muted">{t("loading")}</div>;

  const closePara = async (decision) => {
    const remarks = window.prompt(t("remarks"));
    if (remarks === null) return;
    await api.post(`/paras/${id}/close/`, { decision, remarks: remarks || "" });
    flash("Para " + decision.toLowerCase() + ".");
    load();
  };

  // Admin-only manual override of the workflow status.
  const changeStatus = async (newStatus) => {
    if (newStatus === para.status) return;
    const remarks = window.prompt("Reason for changing the status (optional):");
    if (remarks === null) return;
    try {
      await api.post(`/paras/${id}/set_status/`, { status: newStatus, remarks: remarks || "" });
      flash("Status updated.");
      load();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Could not update status.");
    }
  };

  const deletePara = async () => {
    if (!window.confirm("Are you sure you want to delete this para? This action cannot be undone.")) return;
    try {
      await api.delete(`/paras/${id}/`);
      navigate("/paras");
    } catch (err) {
      console.error(err);
      window.alert("Failed to delete para.");
    }
  };

  // Admins assign in any zone; a Zonal AC only in their own zone.
  const canAssignZone = (zl) =>
    ["SUPER_ADMIN", "CORP_ADMIN"].includes(user.role) ||
    (user.role === "ZONAL_AC" && user.zone === zl.zone);

  return (
    <div className="page-para-detail">
      <div className="para-detail-header-bar no-print">
        <button className="btn btn-sm" onClick={() => navigate("/paras")} data-testid="back-to-paras-btn">
          ← {t("paras")}
        </button>
        <button className="btn btn-sm btn-outline" onClick={() => window.print()} data-testid="print-para-btn">
          🖨 Print
        </button>
      </div>
      {msg && (
        <div className="badge green" style={{ margin: "10px 0", display: "block" }} data-testid="success-msg">
          {msg}
        </div>
      )}

      {/* Grid Layout */}
      <div className="para-detail-grid">
        {/* Main Content Column */}
        <div className="para-main-content">
          <div className="para-main-card">
            <div className="para-title-section">
              <div className="para-title-row">
                <h2>
                  {para.para_no} — {para.department}
                </h2>
                <div className="para-badges-row">
                  <StatusBadge status={para.status} />
                  {para.escalation_level && (
                    <span className="badge red" style={{ marginLeft: 6 }}>
                      {t("escalation")}: {para.escalation_level}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="para-section-title">
              <span>{t("description")}</span>
              {lang === "ta" && !para.description_ta && (
                <button
                  className="btn btn-sm btn-outline"
                  onClick={handleTranslate}
                  disabled={translating}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "0.85rem",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    borderColor: "#3b82f6",
                    color: "#1e3a8a",
                    cursor: "pointer",
                    background: "transparent",
                    fontWeight: "500",
                    marginLeft: "auto",
                  }}
                >
                  {translating ? (
                    <>
                      <span
                        style={{
                          display: "inline-block",
                          width: "12px",
                          height: "12px",
                          border: "2px solid currentColor",
                          borderTopColor: "transparent",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      மொழிபெயர்க்கப்படுகிறது...
                    </>
                  ) : (
                    "✨ தமிழில் காண்க (Translate)"
                  )}
                </button>
              )}
            </div>
            <p className="para-body-text">
              {lang === "ta" && para.description_ta ? para.description_ta : para.description}
            </p>

            {/* Audit Remarks */}
            {para.audit_remarks && (
              <div className="para-remarks-box">
                <div className="para-remarks-title">{t("auditRemarks")}</div>
                <div className="para-remarks-text">
                  {lang === "ta" && para.audit_remarks_ta ? para.audit_remarks_ta : para.audit_remarks}
                </div>
              </div>
            )}

            {/* Assign Reason */}
            {para.assign_reason && (
              <div className="para-note-box">
                <span style={{ fontSize: "1.1rem" }}>📝</span>
                <div>
                  <strong>{t("assignReason")}:</strong>{" "}
                  {lang === "ta" && para.assign_reason_ta ? para.assign_reason_ta : para.assign_reason}
                </div>
              </div>
            )}

            {/* Closure Info */}
            {para.closure && (
              <div className="para-remarks-box" style={{ background: "#edfdf5", borderLeft: "4px solid #10b981" }}>
                <div className="para-remarks-title" style={{ color: "#10b981" }}>
                  {t("closure")}
                </div>
                <div className="para-remarks-text" style={{ color: "#065f46" }}>
                  {para.closure.decision_display} — {para.closure.remarks} ({para.closure.approved_by_name},{" "}
                  {fmtDate(para.closure.closure_date)})
                </div>
              </div>
            )}
          </div>

          {/* Status Tables Card */}
          <div className="para-table-card">
            {para.assign_type === "COMBINED" ? (
              <>
                <h3>{t("zoneStatus")}</h3>
                <p className="muted">
                  {t("zoneOfficerHint")} The para resolves once every zone is marked completed.
                  <span style={{ marginLeft: 6, fontSize: "0.82rem", color: "#6b7785" }}>
                    (Click a row to see officer details)
                  </span>
                </p>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("zone")}</th>
                        <th>{t("cugNumber")}</th>
                        <th>{t("status")}</th>
                        <th>{t("remarks")}</th>
                        <th className="no-print">{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {para.zone_links.map((z) => {
                        const isActive = zonePopup?.zone?.id === z.id;
                        return (
                          <tr
                            key={z.id}
                            onClick={(e) => {
                              if (isActive) { setZonePopup(null); return; }
                              const rect = e.currentTarget.getBoundingClientRect();
                              setZonePopup({ zone: z, rect });
                            }}
                            style={{
                              cursor: "pointer",
                              background: isActive ? "#eef2ff" : undefined,
                              transition: "background 0.15s",
                            }}
                          >
                            <td><strong>{z.zone_name} ({z.zone_code})</strong></td>
                            <td>{z.cug_number || "—"}</td>
                            <td><StatusBadge status={z.status} /></td>
                            <td>{z.remarks || "—"}</td>
                            <td className="no-print" onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                {z.status === "COMPLETED" ? (
                                  <span style={{ color: "#15803d", fontWeight: 650, fontSize: "0.88rem" }}>✅ Submitted</span>
                                ) : z.officer ? (
                                  <span style={{ color: "#92400e", fontWeight: 650, fontSize: "0.88rem" }}>🟡 Assigned, Not Submitted Yet</span>
                                ) : (
                                  <span style={{ color: "#b91c1c", fontWeight: 650, fontSize: "0.88rem" }}>🔴 Not Assigned Yet</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : para.assign_type === "MULTIPLE" ? (
              <>
                <h3>{t("status")}</h3>
                <p className="muted">
                  Each officer updates their own status. The para resolves once every officer is marked completed.
                </p>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>{t("mobile")}</th>
                        <th>{t("status")}</th>
                        <th>{t("remarks")}</th>
                        <th className="no-print">{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {para.officer_links.map((o) => (
                        <tr key={o.id}>
                          <td>
                            {o.officer_name}
                            {o.officer_designation || o.officer_role_display
                              ? ` · ${o.officer_designation || o.officer_role_display}`
                              : ""}
                          </td>
                          <td>{o.officer_mobile || "—"}</td>
                          <td>
                            <StatusBadge status={o.status} />
                          </td>
                          <td>{o.remarks || "—"}</td>
                          <td className="no-print">
                            {o.status === "COMPLETED" ? (
                              <span className="muted">
                                {o.completed_by_name} · {fmtDate(o.completed_at)}
                              </span>
                            ) : (
                              <span className="muted" style={{ color: "#ef4444" }}>
                                ⚠️ {t("notSubmittedYet")}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                <h3>{t("status")}</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>{t("mobile")}</th>
                        <th>{t("status")}</th>
                        <th className="no-print">{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {para.assigned_officer_detail ? (
                            <>
                              {para.assigned_officer_detail.full_name}
                              {para.assigned_officer_detail.designation ||
                              para.assigned_officer_detail.role_display
                                ? ` · ${
                                    para.assigned_officer_detail.designation ||
                                    para.assigned_officer_detail.role_display
                                  }`
                                : ""}
                            </>
                          ) : (
                            <span className="muted">{t("notAssigned")}</span>
                          )}
                        </td>
                        <td>{para.assigned_officer_detail?.mobile || "—"}</td>
                        <td>
                          <StatusBadge status={para.status} />
                        </td>
                        <td className="no-print">
                          {isAdmin || para.assigned_officer === user.id ? (
                            <select
                              value={para.status}
                              onChange={(e) => changeStatus(e.target.value)}
                              title="Change status"
                              style={{
                                width: "auto",
                                padding: "4px 8px",
                                fontSize: 13,
                              }}
                            >
                              {PARA_STATUSES.map(([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sidebar Column */}
        <div className="para-sidebar">
          {/* Metadata details widget */}
          <div className="sidebar-widget">
            <h4 className="widget-title">Details</h4>
            <div className="meta-list">
              <SidebarItem icon="🔢" label={t("sNo")} value={para.s_no || "—"} />
              <SidebarItem icon="📅" label={t("year")} value={para.audit_year} />
              <SidebarItem icon="🏢" label={t("department")} value={para.department} />
              <SidebarItem icon="💰" label={t("amount")} value={formatINR(para.amount)} />
              <SidebarItem icon="🤝" label={t("assignType")} value={para.assign_type_display} />
              <SidebarItem
                icon="👤"
                label={t("assignedTo")}
                value={
                  para.assign_type === "COMBINED"
                    ? para.zone_links?.map((z) => z.zone_name).join(", ") || "—"
                    : para.assign_type === "MULTIPLE"
                    ? para.officer_links?.map((o) => o.officer_name).join(", ") || "—"
                    : para.assigned_officer_detail?.full_name || "—"
                }
              />
              <SidebarItem
                icon="🗺️"
                label={t("zone") || "Zone"}
                value={
                  para.assign_type === "COMBINED"
                    ? para.zone_links?.map((z) => z.zone_name).join(", ") || "—"
                    : para.assign_type === "MULTIPLE"
                    ? [...new Set(
                        (para.officer_links || [])
                          .map((o) => o.officer_zone_name)
                          .filter(Boolean)
                      )].join(", ") || "—"
                    : para.assigned_officer_detail?.zone_name || "—"
                }
              />
              <SidebarItem icon="📊" label={t("status")} value={para.status_display} className="no-print" />
            </div>
          </div>

          {/* Quick Actions widget */}
          {(isAdmin || canAssign || (canClose && para.status !== "CLOSED")) && (
            <div className="sidebar-widget actions-widget no-print">
              <h4 className="widget-title">{t("actions")}</h4>
              {isAdmin && (
                <>
                  <button className="btn btn-outline" onClick={() => setShowEdit(true)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-outline"
                    style={{ borderColor: "red", color: "red" }}
                    onClick={deletePara}
                  >
                    Delete
                  </button>
                </>
              )}
              {canAssign && (
                <button className="btn btn-primary" onClick={() => setShowAssign(true)}>
                  {t("assign")}
                </button>
              )}
              {canClose && para.status !== "CLOSED" && (
                <>
                  <button className="btn btn-success" onClick={() => closePara("ACCEPTED")}>
                    {t("closePara")}
                  </button>
                  <button className="btn btn-outline" onClick={() => closePara("REJECTED")}>
                    {t("reject")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Print-only: officer reply details */}
      {para.replies && para.replies.length > 0 && (
        <div className="print-only para-replies-print">
          <h3 style={{ margin: "20px 0 10px", fontSize: "14px", color: "#0f172a", fontWeight: 700, borderBottom: "1px solid #cbd5e1", paddingBottom: 6 }}>
            Officer Replies ({para.replies.length})
          </h3>
          {para.replies.map((r) => (
            <div
              key={r.id}
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                padding: "10px 12px",
                marginBottom: 10,
                pageBreakInside: "avoid",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#475569", marginBottom: 6, fontWeight: 600 }}>
                <span>👤 {r.officer_name || "—"}</span>
                <span>{r.submitted_at ? fmtDate(r.submitted_at) : ""}</span>
              </div>
              <div style={{ fontSize: "12.5px", color: "#0f172a", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {r.explanation || "—"}
              </div>
              {r.review_status_display && (
                <div style={{ marginTop: 6, fontSize: "11px", color: "#475569" }}>
                  Review: <strong>{r.review_status_display}</strong>
                  {r.reviewed_by_name && <> — by {r.reviewed_by_name}</>}
                  {r.review_remarks && <div style={{ marginTop: 3, fontStyle: "italic" }}>"{r.review_remarks}"</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAssign && (
        <AssignModal
          para={para}
          onClose={() => setShowAssign(false)}
          onDone={() => {
            setShowAssign(false);
            load();
          }}
        />
      )}

      {showEdit && (
        <ParaForm
          para={para}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            setShowEdit(false);
            load();
          }}
        />
      )}

      {activeZoneLink && (
        <ZoneOfficerAssignModal
          zoneLink={activeZoneLink}
          onClose={() => setActiveZoneLink(null)}
          onDone={() => {
            setActiveZoneLink(null);
            flash("Officer assigned successfully.");
            load();
          }}
        />
      )}

      {/* Zone detail popup — centered on screen */}
      {zonePopup && (() => {
        const z = zonePopup.zone;
        return (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setZonePopup(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 23, 42, 0.35)",
                zIndex: 999,
                backdropFilter: "blur(2px)",
              }}
            />
            {/* Centered popup card */}
            <div
              ref={zonePopupRef}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "min(400px, 90vw)",
                zIndex: 1000,
                background: "#fff",
                borderRadius: "16px",
                boxShadow: "0 24px 64px rgba(0,0,0,0.22), 0 4px 16px rgba(0,0,0,0.10)",
                border: "1px solid #e5e7eb",
                padding: "24px 24px 20px",
                animation: "fadeInDown 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            >
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b" }}>
                    {z.zone_name}
                    {z.zone_code && <span style={{ fontWeight: 400, color: "#6b7785", fontSize: "0.85rem", marginLeft: 6 }}>({z.zone_code})</span>}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#94a3b8", marginTop: 3 }}>Zone Officer Details</div>
                </div>
                <button
                  onClick={() => setZonePopup(null)}
                  style={{
                    background: "#f1f5f9",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748b",
                    fontSize: "1rem",
                    lineHeight: 1,
                    padding: "6px 9px",
                    borderRadius: "8px",
                    fontWeight: 700,
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Divider */}
              <div style={{ borderTop: "1px solid #f1f5f9", marginBottom: 18 }} />

              {/* Fields */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16, fontSize: "0.875rem" }}>
                {/* Officer */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>👤</div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "#94a3b8", letterSpacing: "0.07em", marginBottom: 3 }}>Assigned Officer</div>
                    {z.officer_name ? (
                      <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.95rem" }}>
                        {z.officer_name}
                        {z.officer_designation && <span style={{ fontWeight: 400, color: "#64748b", fontSize: "0.85rem" }}> · {z.officer_designation}</span>}
                      </div>
                    ) : (
                      <div style={{ color: "#ef4444", fontWeight: 600 }}>⚠️ Not assigned yet</div>
                    )}
                  </div>
                </div>

                {/* Mobile */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>📞</div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "#94a3b8", letterSpacing: "0.07em", marginBottom: 3 }}>Mobile / CUG</div>
                    <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.95rem" }}>
                      {z.officer_mobile || z.cug_number || "—"}
                    </div>
                  </div>
                </div>

                {/* Reply status */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#fefce8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>📊</div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "#94a3b8", letterSpacing: "0.07em", marginBottom: 3 }}>Reply Status</div>
                    {z.status === "COMPLETED" ? (
                      <div style={{ color: "#15803d", fontWeight: 700, fontSize: "0.95rem" }}>✅ Reply Submitted</div>
                    ) : z.officer ? (
                      <div style={{ color: "#92400e", fontWeight: 700, fontSize: "0.95rem" }}>🟡 Pending Submission</div>
                    ) : (
                      <div style={{ color: "#b91c1c", fontWeight: 700, fontSize: "0.95rem" }}>🔴 Officer Not Assigned</div>
                    )}
                  </div>
                </div>

                {/* Submitted by (only when completed) */}
                {z.status === "COMPLETED" && z.completed_by_name && (
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>✔️</div>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "#94a3b8", letterSpacing: "0.07em", marginBottom: 3 }}>Submitted By</div>
                      <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.95rem" }}>{z.completed_by_name}</div>
                      <div style={{ fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 }}>{fmtDate(z.completed_at)}</div>
                    </div>
                  </div>
                )}

                {/* Actions inside popup */}
                {z.status !== "COMPLETED" && canAssignZone(z) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
                    <button
                      onClick={() => {
                        setZonePopup(null);
                        setActiveZoneLink(z);
                      }}
                      style={{
                        width: "100%",
                        padding: "10px 16px",
                        background: "#1e3a8a",
                        color: "#fff",
                        border: "none",
                        borderRadius: "10px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        fontSize: "0.9rem",
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
                        transition: "all 0.2s",
                      }}
                    >
                      👤 Assign Officer
                    </button>
                    {z.officer && (
                      <button
                        onClick={() => {
                          setZonePopup(null);
                          setActiveZoneLink(z);
                        }}
                        style={{
                          width: "100%",
                          padding: "10px 16px",
                          background: "#fff",
                          color: "#475569",
                          border: "1px solid #cbd5e1",
                          borderRadius: "10px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          fontSize: "0.9rem",
                          transition: "all 0.2s",
                        }}
                      >
                        ✏️ Edit Officer
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}

function SidebarItem({ icon, label, value, className = "" }) {
  return (
    <div className={`meta-item ${className}`}>
      <div className="meta-icon">{icon}</div>
      <div className="meta-info">
        <span className="meta-label">{label}</span>
        <span className="meta-val">{value}</span>
      </div>
    </div>
  );
}
